import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { useTranslation } from 'react-i18next';
import { 
  Bold, 
  Italic, 
  Underline, 
  Undo2, 
  Redo2, 
  Keyboard, 
  AlertCircle,
  Loader2,
  Sparkles
} from 'lucide-react';
import { useCollaboration } from '../lib/useCollaboration';
import { CollaborationProvider } from '../lib/collaboration';
import { CollaborationBar } from './CollaborationStatus';
import AIMenu from './AIMenu';
import { cn } from '../lib/utils';
import { documentsApi, filesApi } from '../lib/api';
import { getDefaultReactSlashMenuItems } from '@blocknote/react';

interface EditorProps {
  documentId: string;
  initialContent?: string;
  onChange?: (content: string) => void;
  collaborative?: boolean;
}

export default function Editor(props: EditorProps) {
  // Disable collaborative mode for now - use simple API saves only
  const collaborative = false; 
  const { documentId } = props;
  
  // Skip collaboration hook entirely when disabled
  const status = 'connected';
  const users: any[] = [];
  const lastSaved = null;
  const provider = null;

  if (collaborative && status === 'connecting' && !provider) {
    return (
      <div className="flex h-[500px] items-center justify-center rounded-lg border bg-card">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Connecting...</span>
        </div>
      </div>
    );
  }

  return (
    <InteractiveEditor 
      {...props} 
      provider={provider} 
      status={status} 
      users={users} 
      lastSaved={lastSaved} 
    />
  );
}

interface InteractiveEditorProps extends EditorProps {
  provider: CollaborationProvider | null;
  status: string;
  users: any[];
  lastSaved: number | null;
}

function InteractiveEditor({
  documentId,
  initialContent,
  collaborative = true,
  onChange,
  provider,
  status,
  users,
  lastSaved
}: InteractiveEditorProps) {
  const { t, i18n } = useTranslation();
  const [selectedText, setSelectedText] = useState('');
  const [documentText, setDocumentText] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Create BlockNote editor (without Yjs collaboration for simplified protocol)
  const editor = useCreateBlockNote({
    initialContent: initialContent
      ? tryParseContent(initialContent)
      : undefined,
    defaultStyles: true,
    // Note: Real-time CRDT collaboration disabled in favor of simpler sync
    // Connection status still shown via CollaborationBar
    uploadFile: async (file: File) => {
      try {
        const result = await filesApi.upload(file, documentId, 'attachment');
        return result.url;
      } catch (error) {
        console.error('File upload failed:', error);
        throw error;
      }
    },
  });

  // Update document text and counts
  useEffect(() => {
    const updateText = () => {
      const blocks = editor.document;
      const text = blocks.map(block => {
        if ('content' in block && Array.isArray(block.content)) {
          return block.content.map(item => {
            if ('text' in item) return item.text;
            return '';
          }).join('');
        }
        return '';
      }).join('\n');
      
      setDocumentText(text);
      
      const words = text.trim() ? text.trim().split(/\s+/).length : 0;
      setWordCount(words);
      setCharCount(text.length);
    };

    updateText();
    const unsubscribe = editor.onEditorContentChange(updateText);

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [editor]);

  // Auto-save logic - save snapshots to DB for initial load
  // Yjs handles real-time sync, this is just for persistence
  // Shared save routine used by autosave and manual retry
  const saveDocument = async () => {
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const content = JSON.stringify(editor.document);
      await documentsApi.update(documentId, { content });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (error: any) {
      console.error('Save failed:', error);
      setSaveStatus('error');
      setSaveError(error?.message || 'Save failed');
    }
  };

  useEffect(() => {
    if (!collaborative) return;

    let isActive = true;
    const handleChange = () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      setSaveStatus('saving');

      saveTimeoutRef.current = setTimeout(async () => {
        if (!isActive) return;
        await saveDocument();
      }, 3000);
    };

    const unsubscribe = editor.onEditorContentChange(handleChange);
    
    return () => {
      isActive = false;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [editor, documentId, collaborative]);

  // Track selection
  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.toString().trim()) {
        setSelectedText(selection.toString());
      } else {
        setSelectedText('');
      }
    };
    document.addEventListener('selectionchange', handleSelection);
    return () => document.removeEventListener('selectionchange', handleSelection);
  }, []);

  // For non-collaborative mode output
  useEffect(() => {
    if (collaborative || !onChange) return;
    const handleChange = () => {
      onChange(JSON.stringify(editor.document));
    };
    const unsubscribe = editor.onEditorContentChange(handleChange);
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [editor, onChange, collaborative]);

  const handleAIInsert = (text: string) => {
    const paragraph = {
      type: 'paragraph',
      content: [{ type: 'text', text }],
    } as any;

    const currentBlock = editor.getTextCursorPosition().block;
    if (currentBlock) {
      editor.insertBlocks([paragraph], currentBlock, 'after');
    } else {
      const blocks = editor.document;
      if (blocks.length > 0) {
        editor.insertBlocks([paragraph], blocks[blocks.length - 1], 'after');
      } else {
        editor.insertBlocks([paragraph]);
      }
    }
  };

  const isDark = document.documentElement.classList.contains('dark');

  // Manual Toolbar Actions
  const handleUndo = () => {
    // Tiptap undo
    (editor as any)._tiptapEditor.commands.undo();
  };

  const handleRedo = () => {
    // Tiptap redo
    (editor as any)._tiptapEditor.commands.redo();
  };

  return (
    <div className="flex flex-col rounded-lg border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-card/50 backdrop-blur-sm">
        {collaborative && (
          <CollaborationBar
            status={status as any}
            users={users}
            lastSaved={lastSaved}
            onReconnect={() => provider?.reconnect?.()}
          />
        )}
        
        <div className="flex items-center gap-2 px-4 py-2">
          {/* Undo/Redo Buttons */}
          <div className="flex items-center gap-1 border-e pe-3 me-3">
            <button
              onClick={handleUndo}
              className="p-1.5 rounded hover:bg-secondary active:scale-95 text-muted-foreground hover:text-foreground transition-all"
              title={t('editor.undo') || 'Undo (Ctrl+Z)'}
            >
              <Undo2 className="h-4 w-4" />
            </button>
            <button
              onClick={handleRedo}
              className="p-1.5 rounded hover:bg-secondary active:scale-95 text-muted-foreground hover:text-foreground transition-all"
              title={t('editor.redo') || 'Redo (Ctrl+Shift+Z)'}
            >
              <Redo2 className="h-4 w-4" />
            </button>
          </div>

          {/* Counts */}
          <div className="hidden lg:flex items-center gap-2 text-xs text-muted-foreground border-e pe-3 me-3">
            <span>{wordCount} words</span>
          </div>

          {/* Save Status */}
          <div className="flex items-center gap-1.5">
            {saveStatus === 'saving' && (
              <div className="flex items-center gap-1 text-xs text-blue-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="hidden sm:inline">Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className="flex items-center gap-1 text-xs text-green-500">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="hidden sm:inline">Saved</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="flex items-center gap-2 text-xs text-red-500">
                <AlertCircle className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{saveError || 'Save failed'}</span>
                <button
                  onClick={saveDocument}
                  className="underline-offset-2 hover:underline"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* Shortcuts Toggle */}
          <button
            onClick={() => setShowShortcuts(!showShortcuts)}
            className={cn(
              'p-1.5 rounded hover:bg-secondary transition-colors',
              showShortcuts && 'bg-secondary text-primary'
            )}
            title={t('editor.shortcuts') || 'Keyboard shortcuts'}
          >
            <Keyboard className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        
        <div className="px-4 py-2 ms-auto">
          <div className="relative">
            <AIMenu
              selectedText={selectedText}
              documentContent={documentText}
              onInsert={handleAIInsert}
              onReplace={(text) => handleAIInsert(text)}
              language={i18n.language.startsWith('ar') ? 'ar' : 'en'}
            />
          </div>
        </div>
      </div>

      {/* Editor Surface */}
      <div 
        className={cn(
          'min-h-[500px] prose prose-stone dark:prose-invert max-w-none',
          i18n.language === 'ar' && 'text-right'
        )}
        style={{ 
          fontFamily: i18n.language === 'ar' ? 'Cairo, Tajawal, sans-serif' : undefined,
          direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
        }}
      >
        <BlockNoteView
          editor={editor}
          theme={isDark ? 'dark' : 'light'}
          slashMenu={true}
          formattingToolbar={true}
          sideMenu={true}
        />
      </div>

       {/* Shortcuts Panel */}
       {showShortcuts && (
        <div className="border-t bg-muted/50 px-4 py-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            {/* Quick list of shortcuts */}
            <div className="flex items-center gap-2"><kbd className="kbd">Ctrl+Z</kbd> Undo</div>
            <div className="flex items-center gap-2"><kbd className="kbd">Ctrl+Shift+Z</kbd> Redo</div>
            <div className="flex items-center gap-2"><kbd className="kbd">Ctrl+B</kbd> Bold</div>
            <div className="flex items-center gap-2"><kbd className="kbd">/</kbd> Slash Menu</div>
          </div>
        </div>
      )}
    </div>
  );
}

function tryParseContent(content: string) {
  try {
    return JSON.parse(content);
  } catch {
    return undefined;
  }
}

